// Thin fetch wrapper for the iBetterAgent JSON API.
//
// `credentials: 'include'` is essential: the server authenticates with an httpOnly
// session cookie, so every request must send/receive cookies. In dev the CRA app
// (:3000) talks to the API (:3001) via the "proxy" field in client/package.json,
// so the base URL is same-origin ('').  Override with REACT_APP_API_URL if needed.

const BASE = process.env.REACT_APP_API_URL || '';

// Feature flag: until Postgres is provisioned and the auth slice is cut over, the
// app keeps using its localStorage auth. Flip REACT_APP_USE_API=true to route auth
// (and, as later phases land, data) through the server.
export const usingApi = () => String(process.env.REACT_APP_USE_API || '').toLowerCase() === 'true';

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Hard client-side deadline: a hung request must surface an error, never an
// endless spinner (a stalled server once left "Uploading…" spinning forever).
// 60s leaves room for a ~1.4MB upload on a slow connection.
const REQUEST_TIMEOUT_MS = 60000;

async function request(method, path, body) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) } : {}),
    });
  } catch (networkErr) {
    if (networkErr && (networkErr.name === 'TimeoutError' || networkErr.name === 'AbortError')) {
      throw new ApiError('The server took too long to respond — please try again in a moment.', 0);
    }
    throw new ApiError('Network error — could not reach the server.', 0);
  }
  // 204 / empty body tolerance. A non-JSON body (e.g. the dev proxy's plain-text
  // "Error occurred while trying to proxy…" when the API is down, or a gateway
  // error page) must surface a FRIENDLY error — not a raw JSON.parse exception.
  const text = await res.text();
  let data = {};
  if (text) {
    try { data = JSON.parse(text); }
    catch {
      throw new ApiError(
        res.ok ? 'Unexpected server response — please try again.' : `The server is not responding (${res.status}) — please try again in a moment.`,
        res.status,
      );
    }
  }
  if (!res.ok) {
    throw new ApiError(data.error || `Request failed (${res.status})`, res.status);
  }
  return data;
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  patch: (path, body) => request('PATCH', path, body),
  delete: (path) => request('DELETE', path),
};
